import * as React from 'react';
import { Bookmark, RotateCcw, Save, SquareKanban, Table2, Trash2, X } from 'lucide-react';
import type { ViewLayout } from '@clawhire/shared';
import { cn } from '@/lib/utils';
import { toast } from '@/components/ui/toaster';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { SimpleTooltip } from '@/components/ui/tooltip';
import { describeFilter, type FilterDef, type FilterValues } from '@/components/data/filters';
import type { UseSavedViewsResult } from '@/components/data/useSavedViews';

export interface ViewSwitcherProps extends Pick<
  UseSavedViewsResult,
  | 'views'
  | 'activeViewId'
  | 'selectView'
  | 'state'
  | 'setLayout'
  | 'setFilters'
  | 'isDirty'
  | 'reset'
  | 'saveView'
  | 'deleteView'
  | 'isSaving'
> {
  /** Used to label filter chips; unknown keys fall back to `key: value`. */
  filterDefs?: FilterDef[];
  /** Hide the table/board toggle when the entity has no board (partners). */
  layouts?: ViewLayout[];
  /** Hide the save button (viewer role). */
  canSave?: boolean;
  className?: string;
}

/**
 * Tabs of saved views + table/board toggle + "Save view" dialog + active filter chips. Drive it
 * with `useSavedViews(entityType)`:  `<ViewSwitcher {...sv} filterDefs={FILTERS} />`.
 */
export function ViewSwitcher({
  views,
  activeViewId,
  selectView,
  state,
  setLayout,
  setFilters,
  isDirty,
  reset,
  saveView,
  deleteView,
  isSaving,
  filterDefs = [],
  layouts = ['TABLE', 'BOARD'],
  canSave = true,
  className,
}: ViewSwitcherProps) {
  const [saveOpen, setSaveOpen] = React.useState(false);
  const activeView = views.find((v) => v.id === activeViewId) ?? null;

  const removeFilter = (key: string) => {
    const next: FilterValues = { ...state.filters };
    delete next[key];
    setFilters(next);
  };

  const chips = Object.entries(state.filters)
    .map(([key, value]) => ({
      key,
      text: describeFilter(
        filterDefs.find((f) => f.key === key),
        value,
      ),
    }))
    .filter((c): c is { key: string; text: string } => Boolean(c.text));

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      <div className="flex flex-wrap items-center gap-2">
        <div
          role="tablist"
          aria-label="Saved views"
          className="scrollbar-thin flex max-w-full items-center gap-1 overflow-x-auto"
        >
          <ViewTab active={activeViewId === null} onClick={() => selectView(null)}>
            All
          </ViewTab>
          {views.map((v) => (
            <ViewTab key={v.id} active={v.id === activeViewId} onClick={() => selectView(v.id)}>
              {v.name}
              {v.isDefault ? <span className="sr-only"> (default)</span> : null}
            </ViewTab>
          ))}
        </div>

        <div className="ml-auto flex items-center gap-1">
          {isDirty ? (
            <SimpleTooltip label="Discard changes">
              <Button variant="ghost" size="icon-sm" onClick={reset} aria-label="Discard view changes">
                <RotateCcw aria-hidden="true" />
              </Button>
            </SimpleTooltip>
          ) : null}
          {canSave ? (
            <Button variant="outline" size="sm" onClick={() => setSaveOpen(true)} disabled={isSaving}>
              <Save aria-hidden="true" />
              <span className="hidden sm:inline">Save view</span>
            </Button>
          ) : null}
          {canSave && activeView && !activeView.isSystem ? (
            <SimpleTooltip label="Delete this view">
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label={`Delete view ${activeView.name}`}
                onClick={() => {
                  if (window.confirm(`Delete the view "${activeView.name}"?`)) {
                    deleteView(activeView.id)
                      .then(() => toast.success('View deleted'))
                      .catch((e: Error) => toast.error(e.message));
                  }
                }}
              >
                <Trash2 aria-hidden="true" />
              </Button>
            </SimpleTooltip>
          ) : null}
          {layouts.length > 1 ? (
            <div role="group" aria-label="Layout" className="ml-1 inline-flex rounded-md border p-0.5">
              {layouts.includes('TABLE') ? (
                <LayoutButton
                  active={state.layout === 'TABLE'}
                  onClick={() => setLayout('TABLE')}
                  label="Table"
                >
                  <Table2 aria-hidden="true" />
                </LayoutButton>
              ) : null}
              {layouts.includes('BOARD') ? (
                <LayoutButton
                  active={state.layout === 'BOARD'}
                  onClick={() => setLayout('BOARD')}
                  label="Board"
                >
                  <SquareKanban aria-hidden="true" />
                </LayoutButton>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>

      {chips.length > 0 ? (
        <div className="flex flex-wrap items-center gap-1.5" aria-label="Active filters">
          {chips.map((c) => (
            <Badge key={c.key} variant="secondary" className="gap-1 pr-1 font-normal">
              {c.text}
              <button
                type="button"
                className="rounded-sm p-0.5 hover:bg-background/60"
                onClick={() => removeFilter(c.key)}
                aria-label={`Remove filter ${c.text}`}
              >
                <X className="h-3 w-3" aria-hidden="true" />
              </button>
            </Badge>
          ))}
          <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={() => setFilters({})}>
            Clear all
          </Button>
        </div>
      ) : null}

      <SaveViewDialog
        open={saveOpen}
        onOpenChange={setSaveOpen}
        defaultName={activeView && !activeView.isSystem ? activeView.name : ''}
        onSave={async (input) => {
          try {
            await saveView(input);
            toast.success(`View "${input.name}" saved`);
            setSaveOpen(false);
          } catch (e) {
            toast.error(e instanceof Error ? e.message : 'Could not save the view');
          }
        }}
      />
    </div>
  );
}

function ViewTab({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={cn(
        'inline-flex h-8 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md px-3 text-sm transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        active ? 'bg-accent font-medium text-accent-foreground' : 'text-muted-foreground',
      )}
    >
      {active ? <Bookmark className="h-3.5 w-3.5" aria-hidden="true" /> : null}
      {children}
    </button>
  );
}

function LayoutButton({
  active,
  onClick,
  label,
  children,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <Button
      variant={active ? 'secondary' : 'ghost'}
      size="icon-sm"
      className="h-7 w-7"
      onClick={onClick}
      aria-pressed={active}
      aria-label={`${label} layout`}
    >
      {children}
    </Button>
  );
}

function SaveViewDialog({
  open,
  onOpenChange,
  defaultName,
  onSave,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultName: string;
  onSave: (input: { name: string; shared: boolean; isDefault: boolean }) => Promise<void>;
}) {
  const [name, setName] = React.useState(defaultName);
  const [shared, setShared] = React.useState(false);
  const [isDefault, setIsDefault] = React.useState(false);
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    if (open) {
      setName(defaultName);
      setShared(false);
      setIsDefault(false);
    }
  }, [open, defaultName]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    try {
      await onSave({ name: name.trim(), shared, isDefault });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={submit} className="space-y-4">
          <DialogHeader>
            <DialogTitle>Save view</DialogTitle>
            <DialogDescription>Stores the current layout, filters, sorting and columns.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="save-view-name">Name</Label>
            <Input
              id="save-view-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={80}
              autoFocus
              required
            />
          </div>
          <div className="flex items-center justify-between gap-4">
            <Label htmlFor="save-view-shared" className="font-normal">
              Share with the team
            </Label>
            <Switch id="save-view-shared" checked={shared} onCheckedChange={setShared} />
          </div>
          <div className="flex items-center justify-between gap-4">
            <Label htmlFor="save-view-default" className="font-normal">
              Open this view by default
            </Label>
            <Switch id="save-view-default" checked={isDefault} onCheckedChange={setIsDefault} />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" loading={busy} disabled={!name.trim()}>
              Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
